import { Component, inject, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ExecutiveStatsService } from '../../../services/executive-stats.service';
import { differenceInDays } from 'date-fns';
import { TimeSeriesGroupingHelper } from '../../../../../_shared/helpers/chart-grouping/time-series-grouping.helper';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { Subject } from 'rxjs';
import { GraphTrendIconComponent } from '../../../../../_shared/components/graphs/graph-trend-icon/graph-trend-icon.component';
import { AccountFilterComponent } from '../../../../../_shared/components/filters/account-filter/account-filter.component';
import { DateFilterComponent } from '../../../../../_shared/components/filters/date-filter/date-filter.component';
import { AccountFilterData, DateIntervalFilterData, DriverFilterData } from '../../../../../_shared/models';
import { isEmptyObject } from '../../../../../_shared/helpers/object.helper';
import { MultiLineChartComponent, MultiLineChartDataPoint, MultiLineChartSeries } from '../../../../../_shared/components/graphs/multi-line-chart/multi-line-chart.component';
import { ExecutiveStatsRequest } from '../../../../../_shared/models/driver-summary-request.model';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-idle-time-modal',
  standalone: true,
  imports: [MultiLineChartComponent, EmptyStateComponent, GraphTrendIconComponent, AccountFilterComponent, DateFilterComponent, GraphErrorStateComponent, TranslocoDirective, TranslocoPipe],
  templateUrl: './idle-time-modal.component.html',
  styleUrl: './idle-time-modal.component.scss'
})
export class IdleTimeModalComponent implements OnInit {
  private activeModal = inject(NgbActiveModal);
  private executiveStatsService = inject(ExecutiveStatsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private transloco = inject(TranslocoService);

  chartData: MultiLineChartDataPoint[] = [];
  readonly chartHeight = 335;
  private readonly defaultYTicks = [100, 80, 60, 40, 20, 0];
  yTicks: number[] = this.defaultYTicks;
  readonly series: MultiLineChartSeries[] = [
    { key: 'IdleTimePerDay', color: '#EE2737', label: this.transloco.translate('executiveStats.chart.idleTimePerDay'), showDots: false },
    { key: 'Globaltrends', color: '#3873C9', label: this.transloco.translate('executiveStats.chart.globalTrends'), showDots: false }
  ];

  isLoading: boolean = true;
  hasError: boolean = false;
  accountFilter: AccountFilterData;
  dateFilter: DateIntervalFilterData;
  driverFilter: DriverFilterData;
  totalValue: number = 0;
  percentageChange: number = 0;
  private cancelSubject: Subject<void> = new Subject<void>();

  public tooltipFormatter = (value: any): string => {
    return this.formatIdleTime(Number(value));
  };

  public yAxisFormatter = (value: any): string => {
    return this.formatYAxisValue(Number(value));
  };

  constructor() {
    this.accountFilter = {} as AccountFilterData;
    this.dateFilter = {} as DateIntervalFilterData;
    this.driverFilter = {} as DriverFilterData;
  }

  async ngOnInit(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      await this.loadData();
    }
  }

  async onAccountFilterChange(filter: AccountFilterData): Promise<void> {
    this.accountFilter = filter;
    this.updateFilter();
  }

  async onDateFilterChange(filter: DateIntervalFilterData): Promise<void> {
    this.dateFilter = filter;
    this.updateFilter();
  }

  async updateFilter(): Promise<void> {
    if (!isEmptyObject(this.dateFilter) && !isEmptyObject(this.accountFilter)) {
      await this.loadData();
    }
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const request: ExecutiveStatsRequest = {
        driverIds: this.driverFilter.driverIds ?? [],
        accountIds: this.accountFilter.accountIds ?? [],
        timeFrom: this.dateFilter.startDate!,
        timeTo: this.dateFilter.endDate!,
        interval: this.dateFilter.interval
      };

      this.cancelSubject.next();
      const [idleTime, globalIdleTime] = await Promise.all([
        this.executiveStatsService.getIdleTime(request, this.cancelSubject),
        this.executiveStatsService.getGlobalIdleTime(request, this.cancelSubject)
      ]);

      this.chartData = this.processData(
        idleTime.data || [],
        globalIdleTime.data || []
      );
      this.yTicks = this.calculateYTicks(this.chartData);

      if (this.chartData.length > 0) {
        const lastPeriod = this.chartData[this.chartData.length - 1];
        this.totalValue = this.series.reduce((sum, s) => sum + (Number(lastPeriod[s.key]) || 0), 0);

        if (this.chartData.length > 1) {
          const previousPeriod = this.chartData[this.chartData.length - 2];
          const previousValue = this.series.reduce((sum, s) => sum + (Number(previousPeriod[s.key]) || 0), 0);
          if (previousValue > 0) {
            this.percentageChange = ((this.totalValue - previousValue) / previousValue) * 100;
          }
        }
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
          this.chartData = [];
        }
      });
    }
  }

  private processData(idleData: any[], globalData: any[]): MultiLineChartDataPoint[] {
    if (!idleData.length && !globalData.length) return [];

    const datasets = [
      { data: idleData.map(x => ({ totalValue: Number(x.totalValue), timeSeries: new Date(x.timeSeries) })), label: 'IdleTimePerDay' },
      { data: globalData.map(x => ({ totalValue: Number(x.totalValue), timeSeries: new Date(x.timeSeries) })), label: 'Globaltrends' }
    ];

    const daysDifference = differenceInDays(this.dateFilter.endDate!, this.dateFilter.startDate!);

    if (daysDifference === 0) {
      return TimeSeriesGroupingHelper.groupByHours(datasets, true);
    } else if (daysDifference <= 31) {
      return TimeSeriesGroupingHelper.groupByDays(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    } else if (daysDifference > 60) {
      return TimeSeriesGroupingHelper.groupByMonths(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    } else {
      return TimeSeriesGroupingHelper.groupByWeeks(datasets, this.dateFilter.startDate!, this.dateFilter.endDate!);
    }
  }

  private calculateYTicks(chartData: MultiLineChartDataPoint[]): number[] {
    if (!chartData?.length) return this.defaultYTicks;

    const allValues: number[] = [];
    chartData.forEach(point => {
      this.series.forEach(s => {
        const value = Number(point[s.key]) || 0;
        if (value > 0) allValues.push(value);
      });
    });

    if (!allValues.length) return this.defaultYTicks;

    const max = Math.max(...allValues);
    const roundedMax = Math.max(
      Math.ceil(max / 10) * 10,
      Math.ceil(max * 1.1 / 10) * 10
    );

    const tickInterval = roundedMax / 5;
    const ticks: number[] = [];
    for (let i = 0; i <= 5; i++) {
      ticks.push(Math.round(tickInterval * i));
    }

    return ticks.sort((a, b) => b - a);
  }

  private formatIdleTime(value: number): string {
    const { hours, minutes, seconds } = this.secondsToHMS(value);
    const parts = [];
    if (hours > 0) parts.push(`${hours.toLocaleString()} hr${hours > 1 ? 's' : ''}`);
    if (minutes > 0) parts.push(`${minutes.toLocaleString()} min${minutes > 1 ? 's' : ''}`);
    if (seconds > 0) parts.push(`${seconds.toLocaleString()} sec${seconds > 1 ? 's' : ''}`);
    return parts.join(' ') || '0 secs';
  }

  private formatYAxisValue(value: number): string {
    const allValues: number[] = [];
    this.chartData.forEach(point => {
      this.series.forEach(s => {
        const v = Number(point[s.key]) || 0;
        if (v > 0) allValues.push(v);
      });
    });
    const maxValue = allValues.length > 0 ? Math.max(...allValues) : 0;

    if (maxValue < 60) {
      const secs = Math.round(value);
      return `${secs} Sec${secs !== 1 ? 's' : ''}`;
    }
    if (maxValue < 3600) {
      const mins = Math.round(value / 60);
      return `${mins} Min${mins !== 1 ? 's' : ''}`;
    }
    const hrs = Math.round(value / 3600);
    return `${hrs} Hr${hrs !== 1 ? 's' : ''}`;
  }

  private secondsToHMS(seconds: number): { hours: number, minutes: number, seconds: number } {
    const total = Math.max(0, Math.round(Number(seconds || 0)));
    return {
      hours: Math.floor(total / 3600),
      minutes: Math.floor((total % 3600) / 60),
      seconds: total % 60
    };
  }

  formatPercentChange(): string {
    const sign = this.percentageChange > 0 ? '+' : '';
    return `${sign}${this.percentageChange.toLocaleString('default', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}%`;
  }

  isUptrendPositive(): boolean {
    return false;
  }

  formatTotalValue(): string {
    return this.formatIdleTime(this.totalValue);
  }

  hasData(): boolean {
    if (!this.chartData || this.chartData.length === 0) return false;
    return this.chartData.some(point => {
      return this.series.some(s => (Number(point[s.key]) || 0) > 0);
    });
  }

  close(): void {
    this.activeModal.close();
  }
}